import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import Box from 'ui-box';
import { useDebouncedCallback } from 'use-debounce/lib';
import {
  SideSheet,
  ISideSheetProps,
  Button,
  Spinner,
  majorScale,
  Heading,
  NumberInput,
  ControlGroup,
  EmptyInputGroup,
  Checkbox,
  FormField,
  HTMLSelect,
  TextArea,
  useToast,
  Grid,
} from '../../npaper';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { useStores } from '../../stores';
import { ICreditNoteItem } from '../../stores/CreditNoteItemStore';
import {
  CreditNoteReason,
  ICreditNote,
  ICreditNoteSnapshotIn,
} from '../../stores/CreditNoteStore';
import { ProductThumbnail } from '../products/ProductThumbnail';
import { formatCreditNoteReason } from './CreditNotesSection';

export interface ICreditNoteSideSheetProps extends ISideSheetProps {
  creditNote: ICreditNote;
}

export const CreditNoteSideSheet: React.FC<ICreditNoteSideSheetProps> =
  observer(({ creditNote, ...props }) => {
    const { saleItemStore, creditNoteStore, creditNoteItemStore } = useStores();
    const intl = useIntl();

    const [items, setItems] = useState<ICreditNoteItem[]>();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [issuing, setIssuing] = useState(false);

    const toaster = useToast();

    useEffect(() => {
      let isSub = true;
      const creditNoteSnapshot = getSnapshot(creditNote);
      creditNoteItemStore
        .getAll({
          filter: { credit_note: creditNoteSnapshot.id },
        })
        .then((cache) => {
          if (isSub) {
            setItems(Array.from(cache.items));
            setLoading(false);
          }
        });
      return () => {
        isSub = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [creditNote, saleItemStore]);

    const updateCallback = useDebouncedCallback(
      (changes: Partial<ICreditNoteSnapshotIn>) => {
        creditNoteStore
          .updateItem(creditNote, { ...getSnapshot(creditNote), ...changes })
          .catch(toaster.showDefaultError);
      },
      20
    );

    const updateQuantity = async (item: ICreditNoteItem, quantity: number) => {
      setUpdating(true);
      creditNoteItemStore
        .updateItem(item, {
          quantity,
        })
        .then(() => creditNoteStore.getItem(creditNote.id, true))
        .catch(toaster.showDefaultError)
        .finally(() => setUpdating(false));
    };

    const allChecked =
      items &&
      items.reduce(
        (p, n) => p && (n.quantity < 0 || n.creditable_quantity === 0),
        true
      );

    const issueCreditNote = async () => {
      setIssuing(true);
      try {
        await creditNoteStore.issueCreditNote(creditNote);
        toaster.addToast({
          intent: 'success',
          message: intl.formatMessage({
            defaultMessage: 'Credit note has been issued',
          }),
        });
        props.onClose();
      } catch (error) {
        toaster.addToast({
          intent: 'danger',
          message: intl.formatMessage({
            defaultMessage: 'Could not issue credit note',
          }),
        });
        toaster.showDefaultError(error);
      }
      setIssuing(false);
    };

    return (
      <SideSheet
        title="Opret kreditnota"
        isShown={props.isShown}
        onClose={props.onClose}
        onOpened={props.onOpened}
        actions={
          <>
            {!creditNote.is_issued && (
              <Button
                primary
                intent="info"
                isLoading={issuing}
                disabled={updating}
                onClick={() => issueCreditNote()}
              >
                <FormattedMessage defaultMessage="Create credit note" />
              </Button>
            )}
          </>
        }
      >
        <Box
          padding={majorScale(4)}
          width="100%"
          maxWidth="800px"
          marginX="auto"
        >
          {!items || loading ? (
            <Box display="flex" justifyContent="center">
              <Spinner />
            </Box>
          ) : (
            <Box>
              <Box marginBottom={majorScale(2)}>
                <Heading marginBottom={majorScale(2)} size={400}>
                  <FormattedMessage defaultMessage="Details" />
                </Heading>
                <Grid>
                  <Box className="col-span-12 md:col-span-6">
                    <FormField
                      label={intl.formatMessage({ defaultMessage: 'Reason' })}
                      medium
                    >
                      <HTMLSelect
                        onChange={(value) => {
                          updateCallback.callback({
                            reason: value as CreditNoteReason,
                          });
                        }}
                        value={creditNote.reason || undefined}
                        options={Object.values(CreditNoteReason).map(
                          (reason) => ({
                            label: formatCreditNoteReason(intl, reason),
                            value: reason,
                          })
                        )}
                      />
                    </FormField>
                  </Box>
                  <Box className="col-span-12">
                    {/*<FormField label="Intern note" medium>
                      <TextArea rows={2} />
                      </FormField>*/}
                    <FormField
                      label={intl.formatMessage({
                        defaultMessage: 'Note for customer',
                      })}
                      medium
                      hint={intl.formatMessage({
                        defaultMessage:
                          'The note is visible for customers on the credit note',
                      })}
                    >
                      <TextArea
                        rows={2}
                        name="note"
                        defaultValue={creditNote.note || undefined}
                        onBlur={(event) => {
                          updateCallback.callback({
                            note: event.target.value,
                          });
                        }}
                      />
                    </FormField>
                  </Box>
                </Grid>
              </Box>

              <Box marginBottom={majorScale(2)}>
                <Heading size={400}>
                  <FormattedMessage defaultMessage="Lines to credit" />
                </Heading>
              </Box>

              <Box>
                <Box
                  display="flex"
                  paddingBottom={majorScale(1)}
                  alignItems="center"
                  className="border-bottom"
                >
                  <Box flexGrow={1} display="flex" alignItems="center">
                    <Checkbox
                      marginY={0}
                      marginRight={majorScale(1)}
                      onChange={() => {
                        if (allChecked) {
                          Promise.all(
                            items.map((item) => updateQuantity(item, 0))
                          );
                        } else {
                          Promise.all(
                            items.map((item) =>
                              updateQuantity(
                                item,
                                item.creditable_quantity * -1
                              )
                            )
                          );
                        }
                      }}
                      checked={allChecked}
                    />
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="Description" />
                    </Heading>
                  </Box>
                  <Box flexBasis="120px" textAlign="right">
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="Invoiced" />
                    </Heading>
                  </Box>
                  <Box flexBasis="120px" textAlign="right" marginLeft="16px">
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="To credit" />
                    </Heading>
                  </Box>
                  <Box flexBasis="120px" textAlign="right" marginLeft="16px">
                    <Heading size={200}>
                      <FormattedMessage defaultMessage="Amount" />
                    </Heading>
                  </Box>
                </Box>

                {items.map((item) => (
                  <Box
                    key={item.id}
                    paddingY={majorScale(1.5)}
                    display="flex"
                    alignItems="center"
                    className="border-bottom"
                  >
                    <Box marginRight={majorScale(1)}>
                      <Checkbox
                        checked={item.quantity < 0}
                        onChange={(flag) => {
                          if (flag) {
                            updateQuantity(item, item.creditable_quantity * -1);
                          } else if (!flag) {
                            updateQuantity(item, 0);
                          }
                        }}
                      />
                    </Box>
                    <Box flexGrow={1}>
                      {item.sale_item && item.sale_item.product !== null ? (
                        item.sale_item.product ? (
                          <ProductThumbnail
                            product={item.sale_item.product}
                            showName
                            showSKU
                          />
                        ) : (
                          <Spinner size={16} />
                        )
                      ) : (
                        <p>{item.sale_item.title}</p>
                      )}
                    </Box>
                    <Box flexBasis="120px" textAlign="right">
                      <p className="font-mono right">
                        <FormattedUnit
                          unit={item.unit}
                          value={item.creditable_quantity}
                        />
                      </p>
                    </Box>
                    <Box flexBasis="120px" textAlign="right" marginLeft="16px">
                      <ControlGroup margin={0}>
                        <NumberInput
                          marginBottom={0}
                          defaultValue={item.creditable_quantity}
                          value={item.quantity * -1}
                          maximumFractionDigits={2}
                          minimumFractionDigits={0}
                          max={item.creditable_quantity}
                          min={0}
                          onChange={(newQty) => {
                            updateQuantity(item, newQty * -1);
                          }}
                        />
                        <EmptyInputGroup>
                          <FormattedUnit unit={item.sale_item.unit} />
                        </EmptyInputGroup>
                      </ControlGroup>
                    </Box>
                    <Box flexBasis="120px" textAlign="right" marginLeft="16px">
                      <p className="font-mono right">
                        <FormattedNumber
                          format="currency"
                          currency={creditNote.currency}
                          value={item.sub_total}
                        />
                      </p>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box
            marginTop={majorScale(2)}
            display="flex"
            flexDirection="column"
            paddingBottom={majorScale(12)}
          >
            <Box
              marginLeft="auto"
              display="flex"
              alignItems="baseline"
              marginBottom={majorScale(1)}
            >
              <Box is="p" className="medium" width="120px" textAlign="right">
                <FormattedMessage defaultMessage="Subtotal" />
              </Box>
              <Box width="120px" textAlign="right" marginLeft="16px">
                <p className="medium font-mono">
                  <FormattedNumber
                    format="currency"
                    currency={creditNote.currency}
                    value={creditNote.sub_total}
                  />
                </p>
              </Box>
            </Box>

            {creditNote.vat_amounts.map((vatAmount, i) =>
              vatAmount.vat_rate ? (
                <Box
                  marginLeft="auto"
                  display="flex"
                  alignItems="baseline"
                  marginBottom={majorScale(1)}
                  key={vatAmount.vat_rate.id}
                >
                  <Box
                    is="p"
                    className="medium muted"
                    width="120px"
                    textAlign="right"
                  >
                    {vatAmount.vat_rate.display_name} (
                    <FormattedNumber
                      format="percent"
                      value={vatAmount.vat_rate.rate}
                    />
                    )
                  </Box>
                  <Box width="120px" textAlign="right" marginLeft="16px">
                    <p className="medium font-mono muted">
                      <FormattedNumber
                        format="currency"
                        currency="DKK"
                        value={vatAmount.amount}
                      />
                    </p>
                  </Box>
                </Box>
              ) : (
                <Spinner key={'index' + i} size={16} />
              )
            )}
            <Box
              marginLeft="auto"
              display="flex"
              alignItems="baseline"
              marginBottom={majorScale(1)}
            >
              <Box is="p" className="medium" width="120px" textAlign="right">
                <FormattedMessage defaultMessage="Total" />
              </Box>
              <Box width="120px" textAlign="right" marginLeft="16px">
                <p className="medium font-mono">
                  <FormattedNumber
                    format="currency"
                    currency={creditNote.currency}
                    value={creditNote.total}
                  />
                </p>
              </Box>
            </Box>
          </Box>
        </Box>
      </SideSheet>
    );
  });
